import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * A small popup menu under (or, when there is no room below, above) its button. It is drawn on
 * <body>, so a scrolling table or card never cuts it off. Clicking an item (.more-item) closes it.
 */
export function Menu({
  button,
  children,
  minWidth = 200,
  align = 'right',
}: {
  button: (p: { onClick: () => void; open: boolean }) => ReactNode;
  children: ReactNode;
  minWidth?: number;
  align?: 'left' | 'right';
}) {
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLSpanElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<CSSProperties>({ visibility: 'hidden' });

  useLayoutEffect(() => {
    if (!open || !anchor.current || !pop.current) return;
    const r = anchor.current.getBoundingClientRect();
    const h = pop.current.offsetHeight;
    const w = Math.max(minWidth, pop.current.offsetWidth);
    const below = window.innerHeight - r.bottom;
    const up = below < h + 12 && r.top > below;
    const left = align === 'right' ? Math.max(8, r.right - w) : Math.min(r.left, window.innerWidth - w - 8);
    setStyle({ position: 'fixed', left, top: up ? Math.max(8, r.top - h - 6) : r.bottom + 6, minWidth, visibility: 'visible' });
  }, [open, minWidth, align]);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (anchor.current?.contains(t) || pop.current?.contains(t)) return;
      close();
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    const onScroll = (e: Event) => !(pop.current && e.target instanceof Node && pop.current.contains(e.target)) && close();
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  return (
    <span ref={anchor} className="menu-anchor" onClick={(e) => e.stopPropagation()}>
      {button({ open, onClick: () => setOpen((o) => !o) })}
      {open &&
        createPortal(
          <div
            ref={pop}
            className="more-menu floating"
            style={style}
            onClick={(e) => {
              e.stopPropagation();
              if ((e.target as HTMLElement).closest('.more-item')) setOpen(false);
            }}
          >
            {children}
          </div>,
          document.body,
        )}
    </span>
  );
}
